// src/pages/blog/[single_post].jsx

import React, { useState, useEffect } from "react";
import { useRouter } from "next/router";
import {
  doc,
  getDoc,
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore"; // Remove these if Firebase is no longer used
import Image from "next/image";
import ReactHtmlParser from "html-react-parser";
import Meta from "../../components/Meta";
import dynamic from "next/dynamic";
import CategoryItem from "../../components/collectrions/CategoryItem"; // Correct path
import emailjs from "@emailjs/browser"; // Ensure EmailJS is installed
import { db } from "../../config/fireabseConfig";

// Importing Masonry and Lightbox
import Masonry from "react-masonry-css";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css"; // Import the lightbox CSS

// Dynamically import ThreeDViewer with SSR disabled
const ThreeDViewer = dynamic(() => import("../../components/ThreeDViewer"), {
  ssr: false,
  loading: () => <div className="text-center">Loading 3D Viewer...</div>,
});

const Single_post = () => {
  const router = useRouter();
  const { single_post: pid } = router.query;

  const [post, setPost] = useState(null);
  const [relatedElements, setRelatedElements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for Download Request Modal
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    organization: "",
    role: "",
    usagePurpose: "",
  });

  const [formStatus, setFormStatus] = useState({
    loading: false,
    success: null,
    error: null,
  });

  // State for Lightbox Gallery
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);

  useEffect(() => {
    if (pid) {
      const fetchPostAndElements = async () => {
        try {
          // Fetch post data
          const postRef = doc(db, "hbimArticles", pid); // Remove if not using Firebase
          const postSnapshot = await getDoc(postRef); // Remove if not using Firebase
          if (postSnapshot.exists()) {
            // Remove if not using Firebase
            const postData = { id: postSnapshot.id, ...postSnapshot.data() }; // Remove if not using Firebase
            setPost(postData);

            // Fetch related elements
            const elementsRef = collection(db, "elements"); // Remove if not using Firebase
            const q = query(elementsRef, where("hbimArticleLink", "==", pid)); // Remove if not using Firebase
            const elementsSnapshot = await getDocs(q); // Remove if not using Firebase
            const fetchedElements = elementsSnapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }));
            setRelatedElements(fetchedElements);
          } else {
            console.error("Post not found");
            setError("Post not found.");
          }
        } catch (err) {
          console.error("Error fetching post or elements:", err);
          setError("Error fetching post or related elements.");
        } finally {
          setLoading(false);
        }
      };

      fetchPostAndElements();
    }

    return () => {
      // Cleanup if necessary
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pid]);

  // If not using Firebase, replace the above useEffect with your data fetching logic

  if (loading) {
    return <div className="text-center mt-20">Loading...</div>;
  }

  if (error) {
    return <div className="text-center mt-20 text-red-500">{error}</div>;
  }

  if (!post) {
    return <div className="text-center mt-20">Post not found.</div>;
  }

  // Handlers for Download Request Modal
  const handleOpenDownloadModal = () => {
    setIsDownloadModalOpen(true);
  };

  const handleCloseDownloadModal = () => {
    setIsDownloadModalOpen(false);
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      organization: "",
      role: "",
      usagePurpose: "",
    });
    setFormStatus({
      loading: false,
      success: null,
      error: null,
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus({ loading: true, success: null, error: null });

    try {
      // Send Email via EmailJS
      const emailParams = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        organization: formData.organization,
        role: formData.role,
        usagePurpose: formData.usagePurpose,
        title: `${post.title} from HBIM ARCHIVE`, // Include the title of the single post
      };

      await emailjs.send(
        "service_td5jarc", // Replace with your EmailJS Service ID
        "template_cl5regq", // Replace with your EmailJS Template ID
        emailParams,
        "wOmWm38llC8kKk6bQ" // Replace with your EmailJS Public Key
      );

      setFormStatus({
        loading: false,
        success: "Your download request has been submitted successfully!",
        error: null,
      });

      // Optionally, close the modal after a delay
      setTimeout(() => {
        handleCloseDownloadModal();
      }, 3000);
    } catch (error) {
      console.error("Error submitting download request:", error);
      setFormStatus({
        loading: false,
        success: null,
        error:
          "Failed to submit your download request. Please try again later.",
      });
    }
  };

  // Handlers for Lightbox
  const openLightbox = (index) => {
    setPhotoIndex(index);
    setIsLightboxOpen(true);
  };

  return (
    <div>
      <Meta title={`${post.title} || GMJA Arc-Hive`} />

      <div className="pt-[5.5rem] lg:pt-24">
        <section className="relative py-16 md:py-24">
          <picture className="pointer-events-none absolute inset-0 -z-10 dark:hidden">
            <img
              src="/images/gradient_light.jpg"
              alt="gradient"
              className="h-full w-full"
            />
          </picture>
          <div className="container">
            <h1 className="font-display text-jacarta-700 mb-4 text-2xl dark:text-white sm:text-5xl text-center">
              {post.title}
            </h1>
            {/* 3D Model Viewer */}
            {post.glbUrl && (
              <div className="mb-12">
                <ThreeDViewer modelUrl={post.glbUrl} />
              </div>
            )}

            {/* Related Elements using CategoryItem */}
            {relatedElements.length > 0 && (
              <div className="mb-12">
                <h2 className="text-xl font-bold mb-4">Related Elements</h2>
                <CategoryItem items={relatedElements} />
              </div>
            )}

            {/* Download Request Button */}
            <div className="mb-12 text-center">
              <button
                onClick={handleOpenDownloadModal}
                className="px-6 py-3 bg-jacarta-800 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Request Download - HBIM
              </button>
            </div>

            {/* Download Request Modal */}
            {isDownloadModalOpen && (
              <div
                className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
                onClick={handleCloseDownloadModal}
              >
                <div
                  className="bg-white dark:bg-jacarta-700 rounded-lg p-6 w-11/12 max-w-md relative overflow-y-auto"
                  onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside the modal
                >
                  {/* Close Button */}
                  <button
                    onClick={handleCloseDownloadModal}
                    className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white"
                    aria-label="Close modal"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>

                  <h2 className="text-2xl font-semibold mb-4 text-jacarta-700 dark:text-white">
                    Download {post.title}
                  </h2>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* First Name */}
                    <div>
                      <label
                        htmlFor="firstName"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                      >
                        First Name<span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        required
                        value={formData.firstName}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 dark:bg-jacarta-700 dark:border-jacarta-600 dark:text-white"
                        placeholder="John"
                      />
                    </div>

                    {/* Last Name */}
                    <div>
                      <label
                        htmlFor="lastName"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                      >
                        Last Name<span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        required
                        value={formData.lastName}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 dark:bg-jacarta-700 dark:border-jacarta-600 dark:text-white"
                        placeholder="Doe"
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                      >
                        Email<span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 dark:bg-jacarta-700 dark:border-jacarta-600 dark:text-white"
                        placeholder="john.doe@example.com"
                      />
                    </div>

                    {/* Phone */}
                    <div>
                      <label
                        htmlFor="phone"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                      >
                        Phone Number<span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 dark:bg-jacarta-700 dark:border-jacarta-600 dark:text-white"
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>

                    {/* Organization */}
                    <div>
                      <label
                        htmlFor="organization"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                      >
                        Organization<span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="organization"
                        name="organization"
                        required
                        value={formData.organization}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 dark:bg-jacarta-700 dark:border-jacarta-600 dark:text-white"
                        placeholder="Your Organization"
                      />
                    </div>

                    {/* Role */}
                    <div>
                      <label
                        htmlFor="role"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                      >
                        Role<span className="text-red-500">*</span>
                      </label>
                      <select
                        id="role"
                        name="role"
                        required
                        value={formData.role}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border-gray-300 bg-white shadow-sm focus:border-green-500 focus:ring-green-500 dark:bg-jacarta-700 dark:border-jacarta-600 dark:text-white"
                      >
                        <option value="">Select your role</option>
                        <option value="Student">Student</option>
                        <option value="Employee">Employee</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    {/* Usage Purpose */}
                    <div>
                      <label
                        htmlFor="usagePurpose"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                      >
                        What is the purpose of your download?
                        <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        id="usagePurpose"
                        name="usagePurpose"
                        required
                        value={formData.usagePurpose}
                        onChange={handleChange}
                        rows="4"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 dark:bg-jacarta-700 dark:border-jacarta-600 dark:text-white"
                        placeholder="Describe how you intend to use the model..."
                      ></textarea>
                    </div>

                    {/* Submission Status */}
                    {formStatus.success && (
                      <div className="text-green-600 text-sm text-center">
                        {formStatus.success}
                      </div>
                    )}
                    {formStatus.error && (
                      <div className="text-red-600 text-sm text-center">
                        {formStatus.error}
                      </div>
                    )}

                    {/* Buttons */}
                    <div className="flex justify-end space-x-4">
                      <button
                        type="button"
                        onClick={handleCloseDownloadModal}
                        className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 dark:bg-jacarta-600 dark:text-gray-300 dark:hover:bg-jacarta-700"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={formStatus.loading}
                        className={`px-4 py-2 text-white bg-jacarta-900 rounded hover:bg-green-700 focus:outline-none ${
                          formStatus.loading
                            ? "opacity-50 cursor-not-allowed"
                            : ""
                        }`}
                      >
                        {formStatus.loading ? "Submitting..." : "Submit"}
                      </button>
                    </div>
                  </form>
                  {/* Confirmation Message */}
                  {formStatus.success && (
                    <p className="mt-4 text-center text-green-600">
                      Dear User, thank you for your download request! We will
                      process it and get back to you shortly.
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Content */}
            <article className="mb-12 prose dark:prose-dark max-w-none">
              <div className="article-content">
                {ReactHtmlParser(post.content || "")}
              </div>
            </article>

            {/* Enhanced HBIM Gallery Images */}
            {post.hbimGalleryImages && post.hbimGalleryImages.length > 0 && (
              <div className="mb-16">
                <h2 className="text-3xl font-bold mb-8 text-center text-jacarta-700 dark:text-white">
                  Gallery
                </h2>

                {/* Masonry Layout */}
                <Masonry
                  breakpointCols={{
                    default: 3,
                    1024: 3,
                    768: 2,
                    500: 1,
                  }}
                  className="my-masonry-grid"
                  columnClassName="my-masonry-grid_column"
                >
                  {post.hbimGalleryImages.map((imgUrl, index) => (
                    <div
                      key={index}
                      className="relative mb-6 cursor-pointer group"
                      onClick={() => openLightbox(index)}
                    >
                      <Image
                        src={imgUrl}
                        alt={`${post.title} Gallery Image ${index + 1}`}
                        width={600}
                        height={600}
                        className="w-full h-auto object-cover rounded-lg shadow-md transition-transform duration-300 transform group-hover:scale-105"
                        layout="responsive"
                        placeholder="blur"
                        blurDataURL="/images/placeholder.png" // Optional: Add a placeholder image
                      />
                      {/* Overlay */}
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-opacity duration-300 flex items-center justify-center rounded-lg">
                        <svg
                          className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                      </div>
                    </div>
                  ))}
                </Masonry>

                {/* Lightbox */}
                {isLightboxOpen && (
                  <Lightbox
                    mainSrc={post.hbimGalleryImages[photoIndex]}
                    nextSrc={
                      post.hbimGalleryImages[
                        (photoIndex + 1) % post.hbimGalleryImages.length
                      ]
                    }
                    prevSrc={
                      post.hbimGalleryImages[
                        (photoIndex + post.hbimGalleryImages.length - 1) %
                          post.hbimGalleryImages.length
                      ]
                    }
                    onCloseRequest={() => setIsLightboxOpen(false)}
                    onMovePrevRequest={() =>
                      setPhotoIndex(
                        (photoIndex + post.hbimGalleryImages.length - 1) %
                          post.hbimGalleryImages.length
                      )
                    }
                    onMoveNextRequest={() =>
                      setPhotoIndex(
                        (photoIndex + 1) % post.hbimGalleryImages.length
                      )
                    }
                    imageTitle={`${post.title} Gallery Image ${photoIndex + 1}`}
                  />
                )}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default Single_post;
